import React from 'react';
import Icon from '../../../components/AppIcon';

const ProgressIndicator = ({ currentStep = 1 }) => {
  const steps = [
    {
      id: 1,
      title: 'Account Details',
      description: 'Basic information and credentials',
      icon: 'User'
    },
    {
      id: 2,
      title: 'Email Verification',
      description: 'Verify your email address',
      icon: 'Mail'
    },
    {
      id: 3,
      title: 'Security Setup',
      description: 'Configure two-factor authentication',
      icon: 'Shield'
    },
    {
      id: 4,
      title: 'Dashboard Setup',
      description: 'Customize your trading interface',
      icon: 'BarChart3'
    }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success text-success-foreground';
      case 'current':
        return 'bg-primary text-primary-foreground';
      case 'upcoming':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-2">Registration Progress</h3>
        <p className="text-sm text-muted-foreground">Complete all steps to access your trading dashboard</p>
      </div>
      <div className="space-y-4">
        {steps?.map((step, index) => {
          const status = getStepStatus(step?.id);
          const isLast = index === steps?.length - 1;

          return (
            <div key={step?.id} className="relative">
              <div className="flex items-start space-x-4">
                {/* Step Icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getStepClasses(status)}`}>
                  {status === 'completed' ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={step?.icon} size={16} />
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className={`text-sm font-medium ${status === 'current' ? 'text-foreground' : status === 'completed' ? 'text-success' : 'text-muted-foreground'}`}>
                      {step?.title}
                    </h4>
                    {status === 'current' && (
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {step?.description}
                  </p>
                </div>
              </div>
              {/* Connecting Line */}
              {!isLast && (
                <div className="absolute left-5 top-10 w-px h-6 bg-border" />
              )}
            </div>
          );
        })}
      </div>
      {/* Progress Bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span>Progress</span>
          <span>{Math.round((currentStep / steps?.length) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / steps?.length) * 100}%` }}
          />
        </div>
      </div>
      {/* Next Steps */}
      <div className="mt-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">What's Next?</h4>
            <p className="text-xs text-muted-foreground">
              After registration, you'll receive an email verification link. Once verified, you can set up two-factor authentication and customize your dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressIndicator;